import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Mic, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { clientsService, FirmFileTimelineItem } from '@/services/api/clients';

/**
 * Two-sided file history for a client, mirroring the client app's files
 * timeline but framed for the firm: a line runs down the centre, files the
 * FIRM sent/generated sit on the left (blue), files the CLIENT uploaded
 * (received from them) sit on the right (green).
 */
const GENERIC_TITLES = new Set(['File', 'Document', 'Photo', 'Voice message']);

function filePhrase(item: FirmFileTimelineItem): string {
  const m = (item.contentType || '').toLowerCase();
  if (m.startsWith('audio/')) return 'Voice message';
  if (m.startsWith('image/')) return GENERIC_TITLES.has(item.title) ? 'Image' : item.title;
  return item.title;
}

function fileIcon(ct?: string | null) {
  const m = (ct || '').toLowerCase();
  if (m.startsWith('audio/')) return Mic;
  if (m.startsWith('image/')) return ImageIcon;
  return FileText;
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function FirmFilesTimeline({ clientId }: { clientId: string }) {
  const [opening, setOpening] = useState<string | null>(null);

  const { data: items = [], isLoading } = useQuery<FirmFileTimelineItem[]>({
    queryKey: ['client-files-timeline', clientId],
    queryFn: () => clientsService.getClientFilesTimeline(clientId),
    enabled: !!clientId,
  });

  const openFile = async (item: FirmFileTimelineItem) => {
    if (opening) return;
    // Open the tab synchronously inside the click gesture so popup blockers
    // don't reject it, then navigate it once the signed URL resolves.
    const win = window.open('', '_blank');
    setOpening(item.id);
    try {
      const { downloadUrl } = await clientsService.getFileDownloadUrl(item.id);
      if (win) win.location.href = downloadUrl;
      else window.location.href = downloadUrl;
    } catch (e: any) {
      if (win) win.close();
      toast.error(e?.response?.data?.error || 'Could not open file');
    } finally {
      setOpening(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-2">
        <FileText className="h-9 w-9 opacity-40" />
        <p className="text-sm">No files yet. Files sent to and received from this client appear here.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Legend */}
      <div className="flex items-center justify-center gap-5 mb-4 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-sky-500" /> Firm
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Client
        </span>
      </div>

      <div className="relative">
        {/* Centre line */}
        <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-border" />
        <div className="space-y-4">
          {items.map(item => {
            const isClient = item.side === 'client';
            const Icon = fileIcon(item.contentType);
            const isOpening = opening === item.id;
            const accent = isClient
              ? 'border-emerald-300 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/30'
              : 'border-sky-300 dark:border-sky-900/60 bg-sky-50 dark:bg-sky-950/30';
            const dot = isClient ? 'bg-emerald-500' : 'bg-sky-500';
            const iconCls = isClient ? 'text-emerald-600 dark:text-emerald-300' : 'text-sky-600 dark:text-sky-300';
            const card = (
              <button
                onClick={() => openFile(item)}
                disabled={isOpening}
                className={cn('max-w-[94%] w-fit rounded-xl border p-2.5 text-left transition-colors hover:brightness-95 active:brightness-90 disabled:opacity-70', accent, isClient ? 'text-right' : '')}
              >
                <div className={cn('flex items-center gap-1.5', isClient && 'justify-end')}>
                  {!isClient && <Icon className={cn('h-3.5 w-3.5 shrink-0', iconCls)} />}
                  <span className="text-sm font-semibold leading-tight">{filePhrase(item)}</span>
                  {isClient && <Icon className={cn('h-3.5 w-3.5 shrink-0', iconCls)} />}
                </div>
                <p className={cn('text-[11px] mt-0.5 underline decoration-dotted', iconCls)}>
                  {isOpening ? 'Opening…' : 'Open file'}
                </p>
                {item.senderName && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {isClient ? 'from ' : 'by '}{item.senderName}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground/70 mt-0.5">{fmtDateTime(item.occurredAt)}</p>
              </button>
            );
            return (
              <div key={item.id} className="relative flex items-stretch">
                <div className="w-1/2 pr-4 flex justify-end">{!isClient && card}</div>
                <span className={cn('absolute left-1/2 -translate-x-1/2 top-3 h-3 w-3 rounded-full ring-4 ring-background', dot)} />
                <div className="w-1/2 pl-4 flex justify-start">{isClient && card}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
